/**
 * A run's verdict at a glance, from either a live bulk-run item or a stored run, the mode it
 * ran in, and whether its scenario was fabricated.
 */

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { flaggedBucket } from '@/modules/review/verdict-format';
import { comparisonBucket } from '@/modules/review/verdict-readers';
import type { BulkRunDetail } from '@/services/ai-function.service';
import type { BulkRunItem, RunMode, ScenarioKind } from '@/types/websocket.types';
import { KindBadge } from '../kind-badge';
import { MODE_LABELS, MODE_TONES } from './modes';

/** The one rendering of a run's verdict; both derivations below feed it. */
const VerdictBadgeView = ({
	failed,
	comparisonError,
	bucket,
	flagged,
	handedOff = false
}: {
	failed: boolean;
	comparisonError: boolean;
	bucket: string | null;
	flagged: boolean;
	/** The run handed the ticket to a person, so there is no reply for the judge: a reviewer marks it. */
	handedOff?: boolean;
}) => {
	if (failed) return <Badge variant='destructive'>Failed</Badge>;
	if (handedOff)
		return (
			<Badge variant='outline' className='text-muted-foreground'>
				Handed off
			</Badge>
		);
	if (comparisonError)
		return (
			<Badge variant='outline' className='border-amber-300 text-amber-700'>
				Verdict unavailable
			</Badge>
		);
	if (bucket === null)
		return (
			<Badge variant='outline' className='text-muted-foreground'>
				No reply to compare
			</Badge>
		);
	return (
		<Badge
			variant='outline'
			className={cn(flagged ? 'border-amber-300 text-amber-700' : 'border-green-300 text-green-700')}
		>
			{bucket}
		</Badge>
	);
};

/** A person's mark, which stands in for the judge; the judge's own bucket stays in the tooltip. */
const ReviewBadge = ({ item }: { item: BulkRunItem }) => {
	const review = item.review!;
	const correct = review.verdict === 'correct';
	const judge = item.escalation
		? 'it handed the ticket to a person'
		: item.comparisonError
		? 'the judge could not run'
		: item.bucket
			? `judge said ${item.bucket}`
			: 'no reply to compare';
	return (
		<Badge
			variant='outline'
			title={`Marked by ${review.userName ?? 'a reviewer'}; ${judge}`}
			className={cn(correct ? 'border-green-300 text-green-700' : 'border-red-300 text-red-700')}
		>
			{correct ? 'Marked Correct' : 'Marked Incorrect'}
		</Badge>
	);
};

/** The verdict of a live bulk-run item; silent while the item is still unsettled. */
export const VerdictBadge = ({ item }: { item: BulkRunItem }) => {
	if (item.status !== 'done' && item.status !== 'failed') return null;
	if (item.status === 'done' && item.review) return <ReviewBadge item={item} />;
	return (
		<VerdictBadgeView
			failed={item.status === 'failed'}
			comparisonError={Boolean(item.comparisonError)}
			bucket={item.bucket}
			flagged={item.flagged}
			handedOff={Boolean(item.escalation)}
		/>
	);
};

/** The verdict as the stored run itself tells it, for rows with no live bulk-run item behind them. */
export const RunVerdictBadge = ({ detail }: { detail: BulkRunDetail }) => {
	const bucket = comparisonBucket(detail.comparison);
	return (
		<VerdictBadgeView
			failed={detail.error !== null}
			comparisonError={detail.comparisonError !== null}
			bucket={bucket}
			flagged={bucket !== null && flaggedBucket(bucket)}
			handedOff={Boolean(detail.escalation)}
		/>
	);
};

/** Where a scenario's run came from, when not a bulk run: a dataset run or the simulator. */
export const OriginBadge = ({ origin }: { origin: BulkRunDetail['origin'] }) =>
	origin.kind === 'dataset_run' ? (
		<Badge variant='outline' className='border-sky-200 text-[10px] text-sky-700'>
			dataset run
		</Badge>
	) : origin.kind === 'simulator' ? (
		<Badge variant='outline' className='text-[10px] text-muted-foreground'>
			simulator
		</Badge>
	) : null;

/** The mode a run ran in. */
export const ModeBadge = ({ mode }: { mode: RunMode }) => (
	<Badge variant='outline' className={cn('text-[10px]', MODE_TONES[mode])}>
		{MODE_LABELS[mode]}
	</Badge>
);

/** A fabricated scenario in a batch, which runs Mocked even when the batch asked for Live data. */
export const BatchKindBadges = ({ kind, batchMode }: { kind: ScenarioKind | null; batchMode: RunMode }) =>
	kind === 'fabricated' ? (
		<>
			<KindBadge kind={kind} />
			{batchMode === 'grounded' && <ModeBadge mode='simulated' />}
		</>
	) : null;
